/**
 * Global utility to hide the purple caret rectangle in Ant Design multi-select search inputs.
 * This runs once when the app loads and uses MutationObserver to catch any dynamically added selects.
 * 
 * This ensures that ALL multi-select inputs in the app (current and future) will have transparent carets.
 */

export function initializeHideMultiSelectCaret() {
  const hideCaretInInputs = (container: Document | Element = document) => {
    const searchInputs = container.querySelectorAll(
      ".ant-select-selection-search-input, .ant-select-selection-search input, .ant-select-multiple .ant-select-selection-search-input, .ant-select-multiple .ant-select-selection-search input"
    );
    
    searchInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.style.caretColor = "transparent";
        input.style.setProperty("caret-color", "transparent", "important");
        input.style.borderLeft = "none";
        input.style.borderRight = "none";
        input.style.boxShadow = "none";
      }
    });
  };

  // Function to initialize observer
  const setupObserver = () => {
    // Run immediately to catch any existing selects
    hideCaretInInputs();

    // Use MutationObserver to catch any dynamically added selects
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              // Check if the added node itself is a select or contains selects
              if (
                node.classList?.contains("ant-select-multiple") ||
                node.querySelector?.(".ant-select-multiple")
              ) {
                hideCaretInInputs(node);
              }
              // Also check if any child nodes are selects
              hideCaretInInputs(node);
            }
          });
        }
        // Also watch for attribute changes (like when Ant Design adds focused class)
        if (mutation.type === "attributes") {
          const target = mutation.target;
          if (target instanceof Element) {
            if (
              target.classList?.contains("ant-select-multiple") ||
              target.closest?.(".ant-select-multiple")
            ) {
              hideCaretInInputs(target);
            }
          }
        }
      });
    });

    // Observe the entire document for changes
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"], // Only watch for class changes (like ant-select-focused)
      });
    }

    // Also observe when selects are focused/unfocused
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (target instanceof HTMLInputElement) {
        const selectContainer = target.closest(".ant-select-multiple");
        if (selectContainer) {
          hideCaretInInputs(selectContainer);
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);

    return () => {
      observer.disconnect();
      document.removeEventListener("focusin", handleFocusIn);
    };
  };

  // If DOM is already ready, setup immediately
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupObserver);
  } else {
    // DOM is already ready
    setupObserver();
  }

  // Also setup observer when body is available (in case it's not ready yet)
  if (!document.body) {
    const checkBody = setInterval(() => {
      if (document.body) {
        clearInterval(checkBody);
        setupObserver();
      }
    }, 100);
    
    // Cleanup after 10 seconds if body never appears (shouldn't happen)
    setTimeout(() => clearInterval(checkBody), 10000);
  } else {
    setupObserver();
  }
}
